'use client';

import { c as _c } from "react/compiler-runtime";
import type { SessionCapabilities, SessionCapabilityFields } from '@/data/code-sessions/runtime/types';
import type { CodeSkillSource } from '@/data/code-sessions/skills';
import type { TurnActivity } from '@/data/code-sessions/turn-activity';
import type { CodeFormAnswer, CodeFormSubmission, CodePromptSubmission, CodeSessionRow, CodeSessionTurn } from '@/data/code-sessions/types';
import type { CodeSessionAgentEvent, CodeSessionChangedFile, CodeSessionEvent } from '@/lib/code-sessions/types';
import type { FormInfo } from '@opencode/client/promise';
/**
 * Session view-model for /code: combines Query snapshots with the shared
 * controller's live events and optimistic commands. The layout owns realtime
 * delivery; optimistic prompts reconcile only against rendered server content.
 *
 * Adapted from ColeMurray/background-agents (MIT), commit a27cad16eaff97d8e151fb59d7e9325bd0f9d842
 */
import { replaceEqualDeep, useIsMutating, useQuery, useQueryClient } from '@tanstack/react-query';
import { useCallback, useMemo, useState, useSyncExternalStore } from 'react';
import type { BootPhaseState } from './boot-phase';
import type { CodeSession, SessionPart } from './mock-sessions';
import type { ComposerQueueModel } from './queued-prompts';
import type { PromptActions, SessionAction } from './session-action';
import type { SessionCommands } from './session-command-store';
import type { TimelineTurn } from './transcript-model';
import type { SessionFork } from './use-session-fork';
import type { SessionRewind } from './use-session-rewind';
import { foldSandboxBoots } from './boot-phase';
import { relativeWhen } from './code-sidebar-labels';
import { contextTokens } from './context-window';
import { hasStarted, sessionOrigin, SESSION_STATUS } from './mock-sessions';
import { composerQueueModel, deriveWaitingPrompts } from './queued-prompts';
import { promptActions } from './session-action';
import { sessionTurnState } from './session-turn-state';
import { buildTimeline, formWasSeen } from './transcript-model';
import { useLeavingArchivedSession } from './use-archive-session-action';
import { useDisconnectDelay } from './use-disconnect-delay';
import { useSessionFork } from './use-session-fork';
import { useSessionRewind } from './use-session-rewind';
import { turnPromptAttachments, turnSharedFileSummaries } from '@/data/code-sessions/attachments';
import { repositoryRefOf } from '@/data/code-sessions/environment';
import { pendingCodeSessionForms } from '@/data/code-sessions/forms';
import { useCodeSessionMutations, useForkStarting, usePendingRewind, usePendingSessionRestores, usePendingSessionTitles, useSteeringTurnIds } from '@/data/code-sessions/mutations';
import { useCodeSessionPullRequest } from '@/data/code-sessions/pull-request';
import { codeSessionKeys, codeSessionOptions } from '@/data/code-sessions/queries';
import { useQueuedTurnWrites } from '@/data/code-sessions/queue-mutations';
import { capabilitiesOf, isRuntimeReady } from '@/data/code-sessions/runtime/capabilities';
import { getCodeSessionController } from '@/data/code-sessions/session-controller';
import { useCodeSessionSkills } from '@/data/code-sessions/skills';
import { deriveTurnActivity, isSessionWorking, isTerminalActivity } from '@/data/code-sessions/turn-activity';
import { userMessageText } from '@/data/code-sessions/turn-prompt';
import { useListedSession, useListedWork } from '@/data/code-sessions/use-session-running';
import { aiSubscriptionModelOf } from '@/lib/code-sessions/models';
import { mergeSetupEvents } from '@/lib/code-sessions/setup-events';
import { deliveredTurnStatus } from '@/lib/code-sessions/turn-utils';
import { useAuth } from '@/providers/auth-provider';

/** No Edit from here or Fork from here on any prompt bubble. */
const NO_ACTIONS: ReadonlyMap<string, SessionAction> = new Map();

/** No rewind or fork action is allowed until the row loads, while `can` is null. */
function allowedTurnActions(can: SessionCapabilities | null, rewind: SessionRewind, fork: SessionFork): {
  rewind: SessionRewind | undefined;
  edits: ReadonlyMap<string, SessionAction>;
  forks: ReadonlyMap<string, SessionAction>;
} {
  if (can === null) {
    return {
      rewind: undefined,
      edits: NO_ACTIONS,
      forks: NO_ACTIONS
    };
  }
  return {
    rewind,
    edits: rewind.edits,
    forks: fork.forks
  };
}

/**
 * Every flush refolds the transcript into new objects. Returning the previous
 * object wherever nothing changed lets memoized rows, and maps keyed by part,
 * skip all but the streaming part.
 */
class StructuralSharing<T> {
  private previous: T | undefined;
  share(next: T): T {
    this.previous = this.previous === undefined ? next : replaceEqualDeep(this.previous, next);
    return this.previous;
  }
}
export type UseCodeSessionResult = {
  /** View-model combining the session row with live status. */
  session: CodeSession | null;
  /** Raw session row. */
  row: CodeSessionRow | null;
  parts: SessionPart[];
  boot: BootPhaseState[];
  /** The composer's tray; undefined for a session the viewer cannot send to. */
  queue: ComposerQueueModel | undefined;
  forms: FormInfo[];
  isRunning: boolean;
  isStopping: boolean;
  answerSubmissions: Record<string, CodeFormSubmission>;
  /** Sandbox is being resumed; transcript loads once `transcriptReady` lands. */
  resuming: boolean;
  /** Edit from here's confirmation, Undo, and Cancel edit; undefined until the row loads. */
  rewind: SessionRewind | undefined;
  /** Undo, Cancel edit, and the fork actions, for surfaces outside the session view. */
  commands: SessionCommands;
  /** Edit from here and Fork from here keyed by prompt bubble ID. */
  promptActions: ReadonlyMap<string, PromptActions>;
  skillSource: CodeSkillSource;
  isLoading: boolean;
  /** The session detail read failed; cached data may still be available. */
  readError: Error | null;
  /** True once loading finished without a session (drives the 404 state). */
  notFound: boolean;
  prompt: (submission: CodePromptSubmission) => Promise<{
    turn: CodeSessionTurn;
  }>;
  interrupt: () => Promise<unknown>;
  /**
   * Remove for a caller that waits on the server's answer, such as the sidebar chat's Stop, which
   * removes the turns still queued. The composer's tray starts its own through `queue`.
   */
  cancelTurn: (turnId: string) => Promise<{
    turn: CodeSessionTurn;
  }>;
  /** Interrupt the agent's current step so waiting steers run now. */
  sendNow: (turnId: string) => Promise<{
    interrupted: boolean;
  }>;
  isSendingNow: boolean;
  answerForm: (formId: string, answer: CodeFormAnswer) => Promise<unknown>;
  refresh: () => Promise<void>;
};
export type UseCodeSessionOptions = {
  /**
   * The viewer only reads the session. A session cubic owns is always read this way, whatever
   * the caller says, once its row shows it.
   */
  readOnly?: boolean;
  /**
   * The session shows outside its /code page, as in a pull request page's chat, which has its own
   * pull request and no skill picker: it neither looks up the session's pull request nor
   * discovers the session's skills, but still drives the session.
   */
  embedded?: boolean;
  /**
   * Whether to read the session at all; true unless said otherwise. A pull request page's chat
   * keeps its session view mounted while the page's other agent answers, and reads nothing then.
   */
  enabled?: boolean;
};

/** Whether the session is read without being driven: see `UseCodeSessionOptions.readOnly`. */
export function isReadOnlyCodeSession(row: SessionCapabilityFields | null | undefined, options?: UseCodeSessionOptions): boolean {
  return options?.readOnly === true || row != null && !capabilitiesOf(row).send;
}

/**
 * Whether a loaded session looks up its branch's pull request: one the client can send to, on a branch
 * whose row allows a pull request, unless it shows embedded in a page that has its own.
 */
function looksUpPullRequest(row: CodeSessionRow | undefined, can: SessionCapabilities | null, readOnly: boolean, options: UseCodeSessionOptions | undefined): boolean {
  return can !== null && Boolean(row?.sessionBranch) && !readOnly && can.pullRequest && options?.embedded !== true;
}

/**
 * Whether the session is a fork still making its first sandbox. Its copied turns wait behind its
 * setup card until its conversation can be read, so the replies never land between prompts already
 * drawn.
 */
function isForkBooting(row: CodeSessionRow | undefined): boolean {
  return row?.forkedFromSessionId != null && row.status === 'creating' && !row.sandboxGeneration;
}

/**
 * A session's title as the sidebar's row names it: a pending rename first,
 * then its cached row, kept current as the lists refetch. The session page
 * never fetches the list itself.
 */
function useSidebarSessionTitle(sessionId) {
  const $ = _c(4);
  const pendingTitles = usePendingSessionTitles();
  const cachedTitle = useListedSession(sessionId)?.title;
  let t0;
  if ($[0] !== cachedTitle || $[1] !== pendingTitles || $[2] !== sessionId) {
    t0 = sessionId === null ? undefined : pendingTitles.get(sessionId) ?? cachedTitle;
    $[0] = cachedTitle;
    $[1] = pendingTitles;
    $[2] = sessionId;
    $[3] = t0;
  } else {
    t0 = $[3];
  }
  return t0;
}
export function useCodeSession(sessionId: string, options?: UseCodeSessionOptions): UseCodeSessionResult {
  const client = useQueryClient();
  // A fork opened before its request returns shows its pending row; the
  // server has nothing to read until then.
  const forkStarting = useForkStarting(sessionId);
  const query = useQuery({
    ...codeSessionOptions(sessionId),
    enabled: options?.enabled !== false && !forkStarting
  });
  const controller = useMemo(() => getCodeSessionController(client, sessionId), [client, sessionId]);
  const transport = useSyncExternalStore(controller.subscribe, controller.getSnapshot, controller.getSnapshot);
  const shown = useDisconnectDelay(transport.connection === 'live' ? null : sessionId);
  const connection = shown ? transport.connection : 'live';
  const mutations = useCodeSessionMutations(sessionId);
  const queuedTurnWrites = useQueuedTurnWrites(sessionId);
  const pendingSteeringIds = useSteeringTurnIds(sessionId);
  const pendingTitle = usePendingSessionTitles().get(sessionId);
  // Unarchive shows on the page at once, as the sidebar shows it, from the same request in flight.
  const pendingRestore = usePendingSessionRestores().includes(sessionId);
  const leaving = useLeavingArchivedSession(sessionId);
  const startedBySessionId = query.data?.session.startedBySessionId ?? null;
  const startedByTitle = useSidebarSessionTitle(startedBySessionId);
  const pendingRewind = usePendingRewind(sessionId);
  const gitActionPending = useIsMutating({
    mutationKey: codeSessionKeys.mutation(sessionId, 'git')
  }) > 0;
  const creatingPullRequest = useIsMutating({
    mutationKey: codeSessionKeys.mutation(sessionId, 'create-pr')
  }) > 0;
  const isSendingNow = useIsMutating({
    mutationKey: codeSessionKeys.mutation(sessionId, 'send-now')
  }) > 0;
  const viewerProfileId = useAuth().user?.id;
  // This tab's pending prompts are by whoever the session saves the viewer's prompts as.
  const promptAuthorId = controller.conversation.promptAuthor(viewerProfileId);
  const data = query.data ?? null;
  const listed = useListedWork(sessionId, data);
  // What the row lets the client do, once it loads.
  const can = useMemo(() => data === null ? null : capabilitiesOf(data.session), [data]);
  // A read-only session dispatches nothing, discovers no skills for a composer it does not have,
  // and leaves its pull request to the page, which reads it without polling GitHub.
  const readOnly = isReadOnlyCodeSession(data?.session, options);
  const setupOnly = data?.session.setupOnly === true;
  // Discovery belongs to the session, so opening a file or PR panel cannot
  // stop refreshes when the workspace card unmounts. A setup-only session
  // pushes nothing, so its branch never has one.
  const pullRequest = useCodeSessionPullRequest(sessionId, looksUpPullRequest(data?.session, can, readOnly, options));
  // A session the viewer may not read, as a setup session after its installation lost access, is
  // as gone to them as a deleted one.
  const notFound = query.isError && [403, 404].includes((query.error as {
    status?: number;
  }).status ?? 0);
  const environmentReadyRevision = transport.events.findLast(event => event.kind === 'sandbox_status' && event.status === 'ready' || event.kind === 'session_status' && event.transcriptReady)?.seq ?? 0;
  // 2.0.26 activates skill plugins lazily during the first prompt. An
  // inventory fetched just after VM readiness can still be empty; refresh once per ready environment
  // admission confirms activation, without putting discovery on startup's path. Only a delivered
  // prompt confirms it: the turn runner announces `admitted` before the send.
  const activationAdmissionRevision = transport.events.find(event_0 => event_0.kind === 'turn' && event_0.seq > environmentReadyRevision && ['running', 'done'].includes(event_0.status))?.seq ?? 0;
  const readyRevision = Math.max(environmentReadyRevision, activationAdmissionRevision);
  const skillSource = useCodeSessionSkills(readOnly || options?.embedded === true || !can?.skills ? null : data?.session ?? null, data?.resuming === true, readyRevision);
  const [sharedParts] = useState(() => new StructuralSharing<SessionPart[]>());
  const [sharedActivity] = useState(() => new StructuralSharing<TurnActivity[]>());
  const derived = useMemo(() => {
    const agentEvents = transport.events.filter((event_1): event_1 is CodeSessionAgentEvent => event_1.kind === 'agent');
    const turnEvents = transport.events.filter((event_2): event_2 is Extract<CodeSessionEvent, {
      kind: 'turn';
    }> => event_2.kind === 'turn');

    // The saved setup events keep each card once its events leave the log.
    const boots = foldSandboxBoots(mergeSetupEvents(data?.setupEvents, transport.events), {
      fork: Boolean(data?.session.forkedFromSessionId)
    });
    const boot = boots.at(-1)!.phases;
    const pendingForms = pendingCodeSessionForms(data?.transcript?.forms, transport.events, transport.answers);
    const pendingFormIds = new Set(pendingForms.map(form => form.id));
    // OpenCode only docks remaining pending requests. After reply the tool
    // preview must not remount as an unanswered 1/N card.
    const sawForm = formWasSeen(data?.transcript?.forms, agentEvents, transport.answers);
    const messages = data?.transcript?.messages ?? [];
    const savedUserText = new Map(messages.filter(message => message.type === 'user' && userMessageText(message).length > 0).map(message_0 => [message_0.id, userMessageText(message_0)]));
    const turns: TimelineTurn[] = (data?.session.turns ?? []).map(turn => ({
      id: turn.id,
      index: turn.index,
      prompt: turn.prompt,
      authoredText: turn.authoredText,
      attachments: turnPromptAttachments(sessionId, turn),
      inboxId: turn.inboxId,
      // A saved user message proves delivery even if the control-plane row is
      // still admitted. Reloading must not turn active work back into a queue.
      status: deliveredTurnStatus(turn, savedUserText),
      delivery: turn.delivery,
      createdAt: Date.parse(turn.createdAt) || 0,
      startedAt: turn.startedAt == null ? undefined : Date.parse(turn.startedAt) || undefined,
      endedAt: turn.endedAt === null ? null : Date.parse(turn.endedAt) || null,
      errorMessage: turn.errorMessage,
      sharedFiles: turnSharedFileSummaries(turn),
      discardedAt: turn.discardedAt,
      checkInReason: turn.checkInReason,
      messageFrom: turn.messageFrom,
      authorProfileId: turn.authorProfileId
    }));
    const activity = deriveTurnActivity(turns, transport.turnActivityEvents, transport.localDispatches, {
      awaitingInput: pendingForms.length > 0,
      listed
    });
    const waiting = deriveWaitingPrompts({
      sessionId,
      viewerProfileId,
      // Null only before the session loads, when it has no saved turns for an edit to reach.
      queuedEdits: can?.queuedEdits === true,
      turns: data?.session.turns ?? [],
      savedUserText,
      turnEvents,
      activity,
      pendingPrompts: transport.pendingPrompts,
      cancellingTurnIds: transport.cancellingTurnIds,
      cuttingInTurnIds: transport.cuttingInTurnIds
    });
    // Between queued turns the runner ends one and claims the next while the
    // session stays running, so Stop stays up and Enter keeps queueing.
    const isRunning = isSessionWorking(data?.session.status, activity, listed);
    const turnState = data === null ? null : sessionTurnState({
      ...data.session,
      setupOnly
    }, {
      pendingRewind,
      messages: data.transcript?.messages ?? null,
      events: transport.events,
      isRunning,
      hasPendingForm: pendingForms.length > 0,
      gitActionPending: gitActionPending || creatingPullRequest
    });
    const parts = buildTimeline({
      turns: isForkBooting(data?.session) ? [] : turns,
      messages,
      agentEvents: transport.streamEvents,
      turnEvents,
      forms: pendingForms,
      boots,
      pendingPrompts: transport.pendingPrompts,
      promptCreatedAt: transport.promptCreatedAt,
      hidden: turnState?.hidden,
      pendingSteers: waiting.pendingSteers,
      promptAuthorId,
      aiSubscriptionProvider: data === null ? undefined : aiSubscriptionModelOf(data.session.model)?.provider
    }).filter(part => {
      if (part.type !== 'question') {
        return true;
      }
      if (part.formId !== undefined) {
        return pendingFormIds.has(part.formId);
      }
      return !sawForm;
    });
    if (data?.session.status === 'creating' && !parts.some(part_0 => part_0.type === 'boot' && !part_0.replaced)) {
      // A replaced sandbox's boot begins now, below the earlier turns.
      const at = data.session.sandboxGeneration ? parts.length : 0;
      parts.splice(at, 0, {
        type: 'boot',
        id: `boot:${boots.length - 1}`,
        phases: boot
      });
    }
    const visibleParts = parts.filter(part_1 => part_1.type !== 'user' || part_1.id === undefined || !waiting.queuedMessageIds.has(part_1.id));
    // Turns a Stop request is waiting on read as stopping until they end.
    const stoppingTurnIds = new Set(transport.interrupt?.turnIds);
    return {
      agentEvents,
      contextTokens: contextTokens(messages, agentEvents),
      turnEvents,
      turnState,
      boot,
      parts: sharedParts.share(visibleParts),
      isRunning,
      queueRows: waiting.rows,
      forms: pendingForms,
      activity: sharedActivity.share(activity.map(turn_0 => stoppingTurnIds.has(turn_0.id) && !isTerminalActivity(turn_0) ? {
        ...turn_0,
        stopping: true as const
      } : turn_0))
    };
  }, [sessionId, data, can, transport.events, transport.turnActivityEvents, transport.streamEvents, transport.pendingPrompts, transport.promptCreatedAt, transport.localDispatches, transport.cancellingTurnIds, transport.cuttingInTurnIds, transport.interrupt, transport.answers, pendingRewind, gitActionPending, creatingPullRequest, setupOnly, sharedParts, sharedActivity, viewerProfileId, promptAuthorId, listed]);
  const queue = useMemo<ComposerQueueModel | undefined>(() => readOnly ? undefined : composerQueueModel({
    rows: derived.queueRows,
    steeringTurnIds: pendingSteeringIds,
    steer: mutations.steer.mutate,
    remove: mutations.cancel.mutate,
    writes: queuedTurnWrites
  }), [readOnly, derived.queueRows, pendingSteeringIds, mutations.steer.mutate, mutations.cancel.mutate, queuedTurnWrites]);
  const rewind = useSessionRewind({
    sessionId,
    state: derived.turnState
  });
  const fork = useSessionFork(sessionId, derived.turnState);
  const commands = useMemo<SessionCommands>(() => ({
    ...rewind.commands,
    forkLatest: fork.latest,
    forkFromMessage: fork.fromMessage
  }), [rewind.commands, fork.latest, fork.fromMessage]);
  // Prompt bubbles get only what the row allows. The commands above stay whole, because the
  // sidebar's menu offers them only on a row its list has loaded.
  const allowed = allowedTurnActions(can, rewind, fork);
  const actionsByPrompt = useMemo(() => promptActions(allowed.edits, allowed.forks), [allowed.edits, allowed.forks]);

  // The turns the agent is on now: what Stop interrupts.
  const workingTurnIds = useMemo(() => derived.activity.filter(turn_1 => turn_1.status === 'running' || turn_1.status === 'waiting').map(turn_2 => turn_2.id), [derived.activity]);
  const awaitingAnswer = derived.forms.length > 0;
  // A steer sent while the agent works cuts in within this request, as Send
  // now does: OpenCode would otherwise hold it until the agent's current step
  // ends, and a step can be a test run that takes minutes.
  const prompt = useCallback((submission: CodePromptSubmission) => mutations.prompt.mutateAsync({
    ...submission,
    // A queued prompt waits for the turn to end, and a turn waiting on a
    // question never ends on its own. A message sent instead of an answer
    // goes to the agent now.
    delivery: awaitingAnswer ? 'steer' : submission.delivery,
    turnId: submission.turnId ?? crypto.randomUUID(),
    authorProfileId: promptAuthorId
  }), [awaitingAnswer, mutations.prompt.mutateAsync, promptAuthorId]);
  const answerForm = useCallback<UseCodeSessionResult['answerForm']>((formId, answer) => mutations.answer.mutateAsync({
    formId,
    answer
  }), [mutations.answer.mutateAsync]);
  const interrupt = useCallback(() => mutations.interrupt.mutateAsync(workingTurnIds), [workingTurnIds, mutations.interrupt.mutateAsync]);
  const session = useMemo<CodeSession | null>(() => {
    if (data === null || can === null) {
      return null;
    }
    const statusOverride = derived.turnEvents[derived.turnEvents.length - 1]?.status;
    const status = data.session.status === 'failed' || statusOverride === 'failed' ? SESSION_STATUS.FAILED : derived.isRunning || data.session.status === 'creating' ? SESSION_STATUS.RUNNING : SESSION_STATUS.DONE;
    // A rewound turn's file totals no longer describe the workspace.
    const hidden = derived.turnState?.hidden;
    const latestTurnWithFiles = [...derived.turnEvents].reverse().find(event_3 => event_3.changedFiles !== undefined && !hidden?.has(event_3.turnId));
    // Turn events expire, and a fork has none of its own; saved turns keep their files.
    const latestDoneTurn = data.session.turns.findLast(turn_3 => turn_3.status === 'done' && !hidden?.has(turn_3.id));
    const latestFiles = latestTurnWithFiles?.changedFiles ?? (Array.isArray(latestDoneTurn?.changedFiles) ? latestDoneTurn.changedFiles as CodeSessionChangedFile[] : undefined);
    return {
      id: data.session.id,
      title: pendingTitle ?? data.session.title,
      repo: data.session.repoFullName,
      repositoryRef: repositoryRefOf(data.session),
      branch: data.session.sessionBranch,
      sideAgentParentId: data.session.sideAgentParentId,
      can,
      imageRoom: data.imageRoom,
      ...(startedBySessionId === null ? {} : {
        startedBy: {
          sessionId: startedBySessionId,
          title: startedByTitle
        }
      }),
      rewindTurnId: derived.turnState?.policy.rewindTurnId ?? null,
      // A fork opened from its parent's list row alone has no model until the server answers.
      model: data.session.model || undefined,
      ...(derived.contextTokens === null ? {} : {
        contextTokens: derived.contextTokens
      }),
      approvalMode: data.session.approvalMode,
      pullRequest: pullRequest.data,
      status,
      archived: data.session.status === 'archived' && !pendingRestore && !leaving,
      restoring: data.session.status === 'archived' && pendingRestore,
      started: hasStarted(data.session.status),
      workspaceReady: isRuntimeReady(data),
      ...sessionOrigin(data.session),
      ...(data.session.findingId ? {
        findingId: data.session.findingId
      } : {}),
      // From the row, so whose session it is stays known when its people could not be read.
      createdByProfileId: data.session.createdByProfileId,
      ...(data.people ? {
        people: data.people,
        createdBy: data.people.find(person => person.owner) ?? null
      } : {}),
      ...(data.session.checkInDueAt ? {
        checkInDueAt: data.session.checkInDueAt
      } : {}),
      mcpToolsRunAs: data.mcpToolsRunAs,
      aiSubscription: data.aiSubscription,
      when: relativeWhen(data.session.lastActivityAt),
      workspace: latestFiles ? {
        files: latestFiles.map(file => ({
          path: file.path,
          status: file.status === 'added' || file.status === 'untracked' ? 'added' : file.status === 'deleted' ? 'deleted' : file.status === 'renamed' ? 'renamed' : 'modified',
          additions: file.additions ?? 0,
          deletions: file.deletions ?? 0,
          ...(file.additions === null || file.deletions === null ? {
            totalsIncomplete: true
          } : {})
        }))
      } : undefined,
      connection,
      parts: derived.parts,
      activity: derived.activity
    };
  }, [can, connection, data, derived, leaving, pendingRestore, pendingTitle, pullRequest.data, startedBySessionId, startedByTitle]);
  return {
    session,
    row: data?.session ?? null,
    parts: derived.parts,
    boot: derived.boot,
    queue,
    forms: derived.forms,
    isRunning: derived.isRunning,
    isStopping: transport.interrupt !== null,
    answerSubmissions: transport.answers,
    resuming: data?.resuming === true,
    rewind: allowed.rewind,
    commands,
    promptActions: actionsByPrompt,
    skillSource,
    // Without a pending row, a starting fork shows as loading, not missing.
    isLoading: query.isLoading || forkStarting,
    readError: query.error,
    notFound,
    prompt,
    interrupt,
    cancelTurn: mutations.cancel.mutateAsync,
    sendNow: mutations.sendNow.mutateAsync,
    isSendingNow,
    answerForm,
    refresh: controller.refresh
  };
}